import type {
  CreateDeadlineDto,
  DeadlineAiPreviewDto,
  DeadlineAiQuotaDto,
  DeadlineArchiveReason,
  DeadlineAssigneeCandidateDto,
  DeadlineBucket,
  DeadlineDetailDto,
  DeadlineDomainValue,
  DeadlineSummaryDto,
  DeadlinesPageDto,
  DeadlineValiditySuggestionDto,
  TenantDeadlineSameTypeSummaryResponse,
  UpdateDeadlineDto,
} from '@prospero/shared';
import { DEADLINE_AI_PREVIEW_TIMEOUT_MS } from '@prospero/shared';
import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';

export interface DeadlinesListFilters {
  withoutDocument?: boolean;
  archived?: boolean;
  bucket?: DeadlineBucket;
  q?: string;
  assigneeUserId?: string;
  domain?: DeadlineDomainValue;
  /** `YYYY-MM-DD`: scadenze dal giorno indicato (incluso). */
  dueFrom?: string;
  /** `YYYY-MM-DD`: scadenze fino al giorno indicato (incluso). */
  dueTo?: string;
  page?: number;
  pageSize?: number;
}

export interface RenewDeadlineInput {
  /** Assente per le scadenze uniche rinnovate senza documento, o quando il file è già in anteprima. */
  file: File | null;
  /** Documento già caricato dall'anteprima: al posto del file. */
  uploadToken?: string;
  /** `YYYY-MM-DD` */
  issuedAt?: string;
  /** `YYYY-MM-DD`: nuova scadenza esplicita (scadenze uniche). */
  dueAt?: string;
  /** Assente quando la validità è già nota al server (riga o catalogo). */
  validityMonths?: number;
  /** undefined = invariata, null = togli la persona. */
  assigneeUserId?: string | null;
}

export interface TakeOverInput {
  /** `YYYY-MM-DD` */
  until: string;
  note?: string;
}

export interface IDeadlineService {
  findAll(filters?: DeadlinesListFilters): Promise<DeadlinesPageDto>;
  findOne(deadlineId: string): Promise<DeadlineDetailDto>;
  findAssigneeCandidates(): Promise<DeadlineAssigneeCandidateDto[]>;
  create(dto: CreateDeadlineDto): Promise<DeadlineSummaryDto>;
  /**
   * Salva il file in deposito temporaneo e lo fa leggere dall'IA: non crea nulla. `signal` annulla
   * la richiesta quando il file non serve più (il server interrompe la lettura).
   */
  previewDocument(file: File, signal?: AbortSignal): Promise<DeadlineAiPreviewDto>;
  /** Stesso deposito temporaneo di `previewDocument`, senza lettura IA né quota: risponde `NOT_READ`. */
  stageDocument(file: File, signal?: AbortSignal): Promise<DeadlineAiPreviewDto>;
  /** Solo se la lettura automatica della settimana è finita: il numero di letture rimaste non serve. */
  getAiQuota(): Promise<DeadlineAiQuotaDto>;
  update(deadlineId: string, dto: UpdateDeadlineDto): Promise<DeadlineSummaryDto>;
  getValiditySuggestion(documentTypeId: string): Promise<DeadlineValiditySuggestionDto>;
  /** Scadenze già presenti di quel tipo nel tenant attivo: solo per l'avviso informativo. */
  getSameTypeSummary(
    documentTypeId: string,
    assigneeUserId?: string,
  ): Promise<TenantDeadlineSameTypeSummaryResponse>;
  renew(deadlineId: string, input: RenewDeadlineInput): Promise<DeadlineSummaryDto>;
  archive(deadlineId: string, reason?: DeadlineArchiveReason): Promise<DeadlineSummaryDto>;
  restore(deadlineId: string): Promise<DeadlineSummaryDto>;
  remove(deadlineId: string, reason?: string): Promise<void>;
  takeOver(deadlineId: string, input: TakeOverInput): Promise<DeadlineSummaryDto>;
  cancelTakeOver(deadlineId: string): Promise<DeadlineSummaryDto>;
  downloadHistoryPdf(deadlineId: string): Promise<Blob>;
}

function buildDeadlinesQuery(filters: DeadlinesListFilters): string {
  const params = new URLSearchParams();
  if (filters.withoutDocument) {
    params.set('withoutDocument', 'true');
  }
  if (filters.archived) {
    params.set('archived', 'true');
  }
  if (filters.bucket) {
    params.set('bucket', filters.bucket);
  }
  if (filters.q?.trim()) {
    params.set('q', filters.q.trim());
  }
  if (filters.assigneeUserId) {
    params.set('assigneeUserId', filters.assigneeUserId);
  }
  if (filters.domain) {
    params.set('domain', filters.domain);
  }
  if (filters.dueFrom) {
    params.set('dueFrom', filters.dueFrom);
  }
  if (filters.dueTo) {
    params.set('dueTo', filters.dueTo);
  }
  if (filters.page) {
    params.set('page', String(filters.page));
  }
  if (filters.pageSize) {
    params.set('pageSize', String(filters.pageSize));
  }
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

@Injectable()
class DeadlineService implements IDeadlineService {
  constructor(@inject(HttpService) private readonly httpService: IHttpService) {}

  async findAll(filters: DeadlinesListFilters = {}): Promise<DeadlinesPageDto> {
    return this.httpService.get<DeadlinesPageDto>(`deadlines${buildDeadlinesQuery(filters)}`);
  }

  async findOne(deadlineId: string): Promise<DeadlineDetailDto> {
    return this.httpService.get<DeadlineDetailDto>(`deadlines/${deadlineId}`);
  }

  async findAssigneeCandidates(): Promise<DeadlineAssigneeCandidateDto[]> {
    return this.httpService.get<DeadlineAssigneeCandidateDto[]>('deadlines/assignee-candidates');
  }

  async create(dto: CreateDeadlineDto): Promise<DeadlineSummaryDto> {
    return this.httpService.post<DeadlineSummaryDto>('deadlines', dto);
  }

  async previewDocument(file: File, signal?: AbortSignal): Promise<DeadlineAiPreviewDto> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpService.postForm<DeadlineAiPreviewDto>('deadlines/ai-preview', formData, {
      timeout: DEADLINE_AI_PREVIEW_TIMEOUT_MS,
      signal,
    });
  }

  async stageDocument(file: File, signal?: AbortSignal): Promise<DeadlineAiPreviewDto> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpService.postForm<DeadlineAiPreviewDto>('deadlines/upload-staging', formData, {
      timeout: DEADLINE_AI_PREVIEW_TIMEOUT_MS,
      signal,
    });
  }

  async getAiQuota(): Promise<DeadlineAiQuotaDto> {
    return this.httpService.get<DeadlineAiQuotaDto>('deadlines/ai-preview/quota');
  }

  async update(deadlineId: string, dto: UpdateDeadlineDto): Promise<DeadlineSummaryDto> {
    return this.httpService.patch<DeadlineSummaryDto>(`deadlines/${deadlineId}`, dto);
  }

  async getValiditySuggestion(documentTypeId: string): Promise<DeadlineValiditySuggestionDto> {
    const query = new URLSearchParams({ documentTypeId }).toString();
    return this.httpService.get<DeadlineValiditySuggestionDto>(
      `deadlines/validity-suggestion?${query}`,
    );
  }

  async getSameTypeSummary(
    documentTypeId: string,
    assigneeUserId?: string,
  ): Promise<TenantDeadlineSameTypeSummaryResponse> {
    const query = new URLSearchParams({
      documentTypeId,
      ...(assigneeUserId ? { assigneeUserId } : {}),
    }).toString();
    return this.httpService.get<TenantDeadlineSameTypeSummaryResponse>(
      `deadlines/same-type-summary?${query}`,
    );
  }

  async renew(deadlineId: string, input: RenewDeadlineInput): Promise<DeadlineSummaryDto> {
    const formData = new FormData();
    if (input.file) {
      formData.append('file', input.file);
    }
    if (input.uploadToken) {
      formData.append('uploadToken', input.uploadToken);
    }
    const fields: Array<[string, string | undefined]> = [
      ['issuedAt', input.issuedAt],
      ['dueAt', input.dueAt],
      [
        'validityMonths',
        input.validityMonths === undefined ? undefined : String(input.validityMonths),
      ],
    ];
    for (const [name, value] of fields) {
      if (value !== undefined) {
        formData.append(name, value);
      }
    }
    if (input.assigneeUserId !== undefined) {
      formData.append('assigneeUserId', input.assigneeUserId ?? '');
    }
    return this.httpService.postForm<DeadlineSummaryDto>(`deadlines/${deadlineId}/renew`, formData);
  }

  async archive(deadlineId: string, reason?: DeadlineArchiveReason): Promise<DeadlineSummaryDto> {
    return this.httpService.post<DeadlineSummaryDto>(
      `deadlines/${deadlineId}/archive`,
      reason ? { reason } : {},
    );
  }

  async restore(deadlineId: string): Promise<DeadlineSummaryDto> {
    return this.httpService.post<DeadlineSummaryDto>(`deadlines/${deadlineId}/restore`);
  }

  async remove(deadlineId: string, reason?: string): Promise<void> {
    const query = reason ? `?${new URLSearchParams({ reason }).toString()}` : '';
    await this.httpService.delete(`deadlines/${deadlineId}${query}`);
  }

  async takeOver(deadlineId: string, input: TakeOverInput): Promise<DeadlineSummaryDto> {
    return this.httpService.post<DeadlineSummaryDto>(`deadlines/${deadlineId}/take-over`, input);
  }

  async cancelTakeOver(deadlineId: string): Promise<DeadlineSummaryDto> {
    return this.httpService.post<DeadlineSummaryDto>(`deadlines/${deadlineId}/take-over/cancel`);
  }

  async downloadHistoryPdf(deadlineId: string): Promise<Blob> {
    const response = await this.httpService.getBlob(`deadlines/${deadlineId}/history.pdf`);
    return response.blob();
  }
}

export { DeadlineService };
